import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { Calculator, DollarSign } from 'lucide-react';

const CostEstimator = ({ onOpenConsultation }) => {
  const [projectType, setProjectType] = useState('');
  const [size, setSize] = useState([500]);
  const [quality, setQuality] = useState('');
  const [estimate, setEstimate] = useState(null);

  const calculateEstimate = () => {
    if (!projectType || !quality) return;

    const baseCosts = {
      'kitchen': 150,
      'bathroom': 200,
      'basement': 80,
      'full-renovation': 120,
      'sdu': 180
    };

    const qualityMultipliers = {
      'basic': 1,
      'mid': 1.4,
      'premium': 2
    };

    const basePrice = baseCosts[projectType] || 100;
    const multiplier = qualityMultipliers[quality] || 1;
    const sqft = size[0];
    
    const total = basePrice * sqft * multiplier;
    const low = Math.round(total * 0.85);
    const high = Math.round(total * 1.15);

    setEstimate({ low, high, sqft });
  };

  useEffect(() => {
    if (projectType && quality) {
      calculateEstimate();
    }
  }, [projectType, size, quality]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="py-20 px-6 bg-[rgba(0,0,0,0.02)]">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4">
            Project Cost Estimator
          </h2>
          <p className="text-lg text-[rgb(131,146,140)] max-w-2xl mx-auto">
            Get an instant estimate for your renovation project. For accurate pricing, book a free consultation.
          </p>
        </div>

        <Card className="p-8 bg-white border-[rgba(0,0,0,0.1)] rounded-xl shadow-lg">
          <div className="space-y-6">
            <div>
              <Label className="text-[rgb(0,55,32)] font-semibold mb-2 block">Project Type</Label>
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your project type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kitchen">Kitchen Remodeling</SelectItem>
                  <SelectItem value="bathroom">Bathroom Renovation</SelectItem>
                  <SelectItem value="basement">Basement Finishing</SelectItem>
                  <SelectItem value="full-renovation">Full Home Renovation</SelectItem>
                  <SelectItem value="sdu">SDU Creation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[rgb(0,55,32)] font-semibold mb-2 block">
                Project Size: {size[0]} sq ft
              </Label>
              <Slider
                value={size}
                onValueChange={setSize}
                min={100}
                max={3000}
                step={50}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-[rgb(175,183,180)] mt-1">
                <span>100 sq ft</span>
                <span>3,000 sq ft</span>
              </div>
            </div>

            <div>
              <Label className="text-[rgb(0,55,32)] font-semibold mb-2 block">Quality Level</Label>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger>
                  <SelectValue placeholder="Select finish quality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic - Standard materials & finishes</SelectItem>
                  <SelectItem value="mid">Mid-Range - Quality materials & design</SelectItem>
                  <SelectItem value="premium">Premium - High-end materials & custom work</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {estimate && (
              <div className="mt-8 p-6 bg-gradient-to-br from-[rgba(148,242,127,0.1)] to-[rgba(129,221,103,0.1)] rounded-lg border border-[var(--accent-primary)]">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator className="w-6 h-6 text-[var(--accent-text)]" />
                  <h3 className="text-xl font-bold text-[rgb(0,55,32)]">Estimated Cost Range</h3>
                </div>
                <div className="text-center py-4">
                  <p className="text-4xl md:text-5xl font-bold text-[var(--accent-text)]">
                    {formatCurrency(estimate.low)} - {formatCurrency(estimate.high)}
                  </p>
                  <p className="text-sm text-[rgb(131,146,140)] mt-2">
                    Based on {estimate.sqft} sq ft project
                  </p>
                </div>
                <p className="text-sm text-[rgb(131,146,140)] text-center mt-4">
                  *This is a rough estimate. Actual costs vary based on specific requirements, materials, and site conditions.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <Button 
                onClick={onOpenConsultation}
                className="flex-1 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-12 rounded-full hover:scale-102 transition-transform"
              >
                <DollarSign className="w-5 h-5 mr-2" />
                Get Accurate Quote
              </Button>
              <Button 
                onClick={calculateEstimate}
                variant="outline"
                className="flex-1 h-12 rounded-full border-[rgba(0,0,0,0.2)] hover:bg-[rgba(0,0,0,0.05)]"
              >
                Recalculate
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CostEstimator;
